import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Paper,
  Toolbar,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
  Divider,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import TableHeader from '../../components/Table/Header'
import ErrorMessage from '../ErrorMessage'
import CompetitorTableLoading from './CompetitorsTableLoading'
import CompetitorTableBody from './CompetitorsTableBody'

import { rowsPerPage, rowHeight } from './constants'

const useStyles = makeStyles((theme) => ({
  paper: {
    width: '100%',
  },
  toolbar: {
    padding: theme.spacing(2),
  },
  title: {
    flex: '1 1 100%',
    marginRight: theme.spacing(1),
  },
  formControl: {
    minWidth: 200,
  },
  smallCell: {
    width: '100px',
  },
  extraPadding: {
    paddingRight: '24px',
  },
}))

const groups = [
  { id: 'scoir', label: 'Adjusted Ranking' },
  { id: 'default', label: 'Default Ranking' },
]

const CompetitorTable = ({ competitors, competitors_adjusted, loading, error }) => {
  const styles = useStyles()
  const [currentPage, setCurrentPage] = useState(0)
  const [filter, setFilter] = useState('scoir')
  const currentList = filter === 'scoir' ? competitors_adjusted : competitors

  const handleChangePage = (_event, newPage) => setCurrentPage(newPage)

  const handleFilterChange = (event) => {
    setFilter(event.target.value)
    setCurrentPage(0)
  }

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, (currentList && currentList.length) - currentPage * rowsPerPage)

  return (
    <Paper
      className={styles.paper}
      elevation={0}
    >
      <Toolbar className={styles.toolbar}>
        <Typography
          className={styles.title}
          variant="body1"
          color="textPrimary"
        >
          Top Competitors by Interested Students
        </Typography>
        <FormControl
          className={styles.formControl}
          variant="outlined"
          size="small"
        >
          <Select
            labelId="competitor-table-filter-label"
            id="competitor-table-filter"
            value={filter}
            onChange={handleFilterChange}
          >
            {groups &&
              groups.map((group) => (
                <MenuItem
                  key={group.id}
                  value={group.id}
                >
                  {group.label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Toolbar>
      <Divider light />
      <TableContainer>
        <Table aria-label={'top competitors'}>
          <TableHeader
            columns={[
              { id: 'rank', label: 'Ranking', enableSort: false, headerStyle: styles.smallCell },
              { id: 'institution', label: 'Institution', enableSort: false },
              { id: 'mutual', label: 'Mutual', enableSort: false, align: 'right' },
              { id: 'interested', label: 'Total', enableSort: false, align: 'right', headerStyle: styles.extraPadding },
            ]}
          />

          <TableBody>
            {loading ? (
              <CompetitorTableLoading rowsPerPage={rowsPerPage} />
            ) : error || !currentList || currentList.length === 0 ? (
              <TableRow style={{ height: rowHeight * 5 }}>
                <TableCell
                  colSpan={12}
                  style={{ verticalAlign: 'middle', height: rowHeight * 5 }}
                >
                  <ErrorMessage title="Sorry, we don’t currently have any data for this selection." />
                </TableCell>
              </TableRow>
            ) : (
              <CompetitorTableBody
                competitors={currentList}
                rowsPerPage={rowsPerPage}
                rowHeight={rowHeight}
                currentPage={currentPage}
                emptyRows={emptyRows}
                classNames={{
                  smallCell: styles.smallCell,
                  extraPadding: styles.extraPadding,
                }}
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={currentList?.length || 0}
        rowsPerPage={rowsPerPage}
        page={currentPage}
        onPageChange={handleChangePage}
        rowsPerPageOptions={[rowsPerPage]}
      />
    </Paper>
  )
}

CompetitorTable.propTypes = {
  competitors: PropTypes.array.isRequired,
  competitors_adjusted: PropTypes.array.isRequired,
  error: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
}

export default CompetitorTable
